import { Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, Link } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { useAuth0, Auth0Provider } from "@auth0/auth0-react";
import DashboardLayout from "./layouts/DashboardLayout";
// Auth screens stay eager: they are the first paint of an unauthenticated
// visit and a chunk request in front of them only adds a blank frame.
import AuthCallback from "./pages/AuthCallback";
import Login from "./pages/Login";

// Every other page is a route-level chunk. Opening /borrowers used to download
// the facility cockpit and echarts with it — 2.3 MB in one file. The rule is one
// lazyRoute() per route and nothing else imported eagerly from ./pages, so adding a
// page cannot quietly grow the entry bundle again.
const Dashboard = lazyRoute(() => import("./pages/Dashboard"));
const NewFacility = lazyRoute(() => import("./pages/NewFacility"));
const NewDeal = lazyRoute(() => import("./pages/NewDeal"));
const DealsPage = lazyRoute(() => import("./pages/deal/DealsPage"));
const DealPage = lazyRoute(() => import("./pages/deal/DealPage"));
const FacilityPage = lazyRoute(() => import("./pages/facility/FacilityPage"));
const ConfigPage = lazyRoute(() => import("./pages/ConfigPage"));
const BookTeamPage = lazyRoute(() => import("./pages/BookTeamPage"));
const AlertsPage = lazyRoute(() => import("./pages/AlertsPage"));
const IncidentsPage = lazyRoute(() => import("./pages/IncidentsPage"));
const BorrowersPage = lazyRoute(() => import("./pages/BorrowersPage"));
const ApplicationsPage = lazyRoute(() => import("./pages/ApplicationsPage"));
const BorrowerDetailPage = lazyRoute(() => import("./pages/BorrowerDetailPage"));
const BookDetailPage = lazyRoute(() => import("./pages/BookDetailPage"));
const BorrowerUploadPage = lazyRoute(() => import("./pages/BorrowerUploadPage"));
const BorrowerTeamPage = lazyRoute(() => import("./pages/BorrowerTeamPage"));
const AdminSystemPage = lazyRoute(() => import("./pages/AdminSystemPage"));
const OverviewPage = lazyRoute(() => import("./pages/OverviewPage"));
const ApplyPage = lazyRoute(() => import("./pages/ApplyPage"));
import ProtectedRoute from "./components/ProtectedRoute";
import RequireAdmin from "./components/RequireAdmin";
import { ToastProvider, useToast } from "./lib/toast";
import { setApiErrorHandler } from "./lib/api";
import { lazyRoute } from "./lib/lazyRoute";
import { Button } from "./components/ds";
import { getRole } from "./lib/auth";
import { usePermissions, hasDealsAccess } from "./hooks/usePermissions";
import { BorrowerBookList } from "./components/borrower/BorrowerBookList";
import dashboardStyles from "./pages/dashboard.module.sass";
import styles from "./App.module.css";

const AUTH0_DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN || "";
const AUTH0_CLIENT_ID = import.meta.env.VITE_AUTH0_CLIENT_ID || "";
const AUTH0_AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE || "";

function BorrowerLanding() {
  const perms = usePermissions();
  if (!perms.loaded) return null;
  const readableBooks = perms.books.filter((b) => b.bookView);
  if (hasDealsAccess(perms)) return <Suspense fallback={null}><Dashboard /></Suspense>;
  if (readableBooks.length > 1) {
    return (
      <div className={dashboardStyles.borrowerPage}>
        <div className={`animate-fade-up ${dashboardStyles.header}`}>
          <h1 className={dashboardStyles.pageTitle}>My Books</h1>
        </div>
        <div className="animate-fade-up">
          <BorrowerBookList books={readableBooks} />
        </div>
      </div>
    );
  }
  if (readableBooks.length === 1) return <Navigate to={`/books/${readableBooks[0].bookId}`} replace />;
  if (perms.bookUpload) return <Navigate to="/borrower/upload" replace />;
  if (perms.manageAccess) return <Navigate to="/borrower/team" replace />;
  return <Navigate to="/borrower/upload" replace />;
}

function NotFound() {
  return (
    <div className={`min-h-screen flex flex-col items-center justify-center gap-3 ${styles.notFound}`}>
      <p className={styles.notFoundCode}>404</p>
      <p className={styles.notFoundMessage}>Page not found</p>
      <Link to="/" className={styles.notFoundLink}>Back to home</Link>
    </div>
  );
}

function SentryUserSync() {
  const { user, isAuthenticated } = useAuth0();
  useEffect(() => {
    if (isAuthenticated && user) {
      Sentry.setUser({ email: user.email, username: user.name });
    } else {
      Sentry.setUser(null);
    }
  }, [isAuthenticated, user]);
  return null;
}

function ApiErrorWirer() {
  const toast = useToast();
  useEffect(() => {
    setApiErrorHandler((message, status) => {
      if (status === 401 || status === 403) {
        toast("error", "Unauthorized", message);
      } else if (status >= 500) {
        toast("error", "Server error", message);
      } else {
        toast("error", "Error", message);
      }
    });
  }, [toast]);
  return null;
}

function AppRoutes() {
  const navigate = useNavigate();

  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: `${window.location.origin}/auth/callback`,
        audience: AUTH0_AUDIENCE,
        scope: "openid profile email",
      }}
      onRedirectCallback={(appState) => {
        if (appState?.returnTo) {
          sessionStorage.setItem("auth_return_to", appState.returnTo as string);
        }
        navigate("/auth/callback", { replace: true });
      }}
      cacheLocation="localstorage"
    >
      <ToastProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/apply" element={<Suspense fallback={null}><ApplyPage /></Suspense>} />
          <Route path="/auth/callback" element={<AuthCallback />} />

          <Route element={<ProtectedRoute />}>
            <Route element={<DashboardLayout />}>
              <Route path="/" element={getRole() === "borrower" ? <BorrowerLanding /> : <Dashboard />} />
              <Route path="/books/:id" element={<BookDetailPage />} />
              <Route path="/books/:id/team" element={<BookTeamPage />} />
              <Route path="/borrower/upload" element={<BorrowerUploadPage />} />
              <Route path="/borrower/team" element={<BorrowerTeamPage />} />
              <Route path="/facility/:id" element={<FacilityPage />} />
              {/* /deal/:id is the CONTRACT, and has been since G2. It was the
                  cockpit's address until the facility rename and the redirect
                  that carried those bookmarks to /facility/* came off in the
                  same commit that gave the path its new meaning: a bookmark
                  holding a facility uuid now 404s, which is the point. A URL
                  that changes entity without changing shape is worse than one
                  that breaks. */}
              <Route path="/deal/:id" element={<DealPage />} />
              {/* The index of contracts. Not under RequireAdmin, for the same
                  reason `/deal/:id` is not: `GET /deals` already scopes itself
                  to what the caller's grants reach — a borrower sees the Deals
                  of the books it reads and the ones it was named on, and an
                  empty list is the honest answer to a grant that reaches none.
                  The alta inside it (`/deal/new`) is admin, and the button that
                  leads there only renders for one. */}
              <Route path="/deals" element={<DealsPage />} />

              <Route element={<RequireAdmin />}>
                <Route path="/overview" element={<OverviewPage />} />
                <Route path="/alerts" element={<AlertsPage />} />
                <Route path="/incidents" element={<IncidentsPage />} />
                <Route path="/applications" element={<ApplicationsPage />} />
                <Route path="/borrowers" element={<BorrowersPage />} />
                <Route path="/borrowers/:id" element={<BorrowerDetailPage />} />
                <Route path="/deal/new" element={<NewDeal />} />
                <Route path="/facility/new" element={<NewFacility />} />
                {/* /facility/:id/setup was the pre-HU-046 onboarding wizard. Mapping is
                    book-scoped now and a facility cannot exist before its book is mapped,
                    so the wizard had nothing legitimate to configure, it only had the
                    power to overwrite the book. Old links land on the facility's config. */}
                <Route path="/facility/:id/setup" element={<Navigate to="../config" replace />} />
                <Route path="/facility/:id/config" element={<ConfigPage />} />
                <Route path="/admin/system" element={<AdminSystemPage />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Route>
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
        <ApiErrorWirer />
        <SentryUserSync />
      </ToastProvider>
    </Auth0Provider>
  );
}

/**
 * Without an explicit fallback Sentry.ErrorBoundary renders null, so any
 * uncaught render error turned the tab into a white page with no way out but
 * the browser's reload button. This at least names what happened and offers
 * the reload.
 */
function CrashScreen() {
  return (
    <div className={`min-h-screen flex flex-col items-center justify-center gap-3 ${styles.crash}`}>
      <p className={styles.crashTitle}>Something went wrong</p>
      <p className={styles.crashMessage}>
        The page could not be loaded. Reloading usually fixes it — the error has been reported.
      </p>
      <Button variant="secondary" size="compact" onClick={() => window.location.reload()}>
        Reload
      </Button>
    </div>
  );
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={<CrashScreen />}>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}
